import { SUBSCRIPTION_FILTER_LABELS, SUBSCRIPTION_FILTER_VALUES } from '@/lib/subscription'
import { VALIDATION_STATUS_LABELS, VALIDATION_STATUS_VALUES } from '@/lib/validation'
import { EMPTY_DATE_RANGE, PERIOD_CHIP_KEY, getPeriodChipLabel, hasDateRange, parseDateRange, writeDateRange } from '@/lib/filters'
import type { DateRange } from '@/lib/filters'
import type { CustomerAgent, CustomerValidationStatus, SubscriptionFilter } from '@/api/types'

export type ShipmentAutomationFilter = '' | 'true' | 'false'

export interface CustomerFacetFilters {
  enablePostnord: ShipmentAutomationFilter
  subscriptionStatuses: SubscriptionFilter[]
  validationStatuses: CustomerValidationStatus[]
  agentIds: number[]
  period: DateRange
}

// Ett chip vet själv hur filtren ser ut utan det, så sidan slipper tolka nycklar.
export interface CustomerFacetChip {
  key: string
  label: string
  withoutChip: CustomerFacetFilters
}

export const ENABLE_POSTNORD_PARAM = 'enable_postnord'
export const SUBSCRIPTION_STATUS_PARAM = 'subscription_status'
export const VALIDATION_STATUS_PARAM = 'validation_status'
export const AGENT_ID_PARAM = 'agent_id'

export const SHIPMENT_AUTOMATION_GROUP_LABEL = 'Automatiska leveranser'
export const SUBSCRIPTION_GROUP_LABEL = 'Abonnemang'
export const VALIDATION_GROUP_LABEL = 'Validering'
export const AGENT_GROUP_LABEL = 'Säljare'

export const SHIPMENT_AUTOMATION_OPTIONS: { value: Exclude<ShipmentAutomationFilter, ''>; label: string }[] = [
  { value: 'true', label: 'På' },
  { value: 'false', label: 'Av' }
]

export const EMPTY_FACET_FILTERS: CustomerFacetFilters = {
  enablePostnord: '',
  subscriptionStatuses: [],
  validationStatuses: [],
  agentIds: [],
  period: EMPTY_DATE_RANGE
}

// Okända värden i URL:en ignoreras i stället för att bryta sidan.
export function parseShipmentAutomation(value: string | null): ShipmentAutomationFilter {
  if (value === 'true' || value === 'false') {
    return value
  }

  return ''
}

// Filtrerar genom den kanoniska listan: okända värden försvinner, dubbletter slås ihop och ordningen blir alltid densamma.
function parseSubscriptionStatuses(values: string[]): SubscriptionFilter[] {
  return SUBSCRIPTION_FILTER_VALUES.filter((status) => values.includes(status))
}

function parseValidationStatuses(values: string[]): CustomerValidationStatus[] {
  return VALIDATION_STATUS_VALUES.filter((status) => values.includes(status))
}

function parseAgentIds(values: string[]): number[] {
  const ids = values.map(Number).filter((id) => Number.isInteger(id) && id > 0)

  return [...new Set(ids)]
}

export function parseFacetFilters(params: URLSearchParams): CustomerFacetFilters {
  return {
    enablePostnord: parseShipmentAutomation(params.get(ENABLE_POSTNORD_PARAM)),
    subscriptionStatuses: parseSubscriptionStatuses(params.getAll(SUBSCRIPTION_STATUS_PARAM)),
    validationStatuses: parseValidationStatuses(params.getAll(VALIDATION_STATUS_PARAM)),
    agentIds: parseAgentIds(params.getAll(AGENT_ID_PARAM)),
    period: parseDateRange(params)
  }
}

// Skriver om facetterna från grunden så att avmarkerade val inte ligger kvar i URL:en.
export function writeFacetFilters(params: URLSearchParams, filters: CustomerFacetFilters): void {
  params.delete(ENABLE_POSTNORD_PARAM)
  params.delete(SUBSCRIPTION_STATUS_PARAM)
  params.delete(VALIDATION_STATUS_PARAM)
  params.delete(AGENT_ID_PARAM)

  if (filters.enablePostnord !== '') {
    params.set(ENABLE_POSTNORD_PARAM, filters.enablePostnord)
  }

  for (const status of filters.subscriptionStatuses) {
    params.append(SUBSCRIPTION_STATUS_PARAM, status)
  }

  for (const status of filters.validationStatuses) {
    params.append(VALIDATION_STATUS_PARAM, status)
  }

  for (const agentId of filters.agentIds) {
    params.append(AGENT_ID_PARAM, String(agentId))
  }

  writeDateRange(params, filters.period)
}

// Enda stället där URL-strängen blir en boolean mot API:t.
export function toEnablePostnordParam(value: ShipmentAutomationFilter): boolean | undefined {
  if (value === '') {
    return undefined
  }

  return value === 'true'
}

// Räknar bara det som ligger i Filter-menyn – perioden har en egen knapp.
export function countMenuFacets(filters: CustomerFacetFilters): number {
  const enablePostnordCount = filters.enablePostnord === '' ? 0 : 1

  return enablePostnordCount + filters.subscriptionStatuses.length + filters.validationStatuses.length + filters.agentIds.length
}

export function formatAgentName(agent: CustomerAgent): string {
  return agent.name ?? `Säljare #${agent.id}`
}

// Ett id som inte finns i listan (t.ex. från en gammal länk) får ändå ett läsbart chip.
export function getAgentLabel(agentId: number, agents: CustomerAgent[]): string {
  const agent = agents.find((candidate) => candidate.id === agentId)

  return agent ? formatAgentName(agent) : `Säljare #${agentId}`
}

function getShipmentAutomationLabel(value: Exclude<ShipmentAutomationFilter, ''>): string {
  return SHIPMENT_AUTOMATION_OPTIONS.find((option) => option.value === value)?.label ?? value
}

export function listFacetChips(filters: CustomerFacetFilters, agents: CustomerAgent[]): CustomerFacetChip[] {
  const chips: CustomerFacetChip[] = []

  if (filters.enablePostnord !== '') {
    chips.push({
      key: ENABLE_POSTNORD_PARAM,
      label: `${SHIPMENT_AUTOMATION_GROUP_LABEL}: ${getShipmentAutomationLabel(filters.enablePostnord)}`,
      withoutChip: { ...filters, enablePostnord: '' }
    })
  }

  for (const status of filters.subscriptionStatuses) {
    chips.push({
      key: `${SUBSCRIPTION_STATUS_PARAM}:${status}`,
      label: `${SUBSCRIPTION_GROUP_LABEL}: ${SUBSCRIPTION_FILTER_LABELS[status]}`,
      withoutChip: { ...filters, subscriptionStatuses: filters.subscriptionStatuses.filter((candidate) => candidate !== status) }
    })
  }

  for (const status of filters.validationStatuses) {
    chips.push({
      key: `${VALIDATION_STATUS_PARAM}:${status}`,
      label: `${VALIDATION_GROUP_LABEL}: ${VALIDATION_STATUS_LABELS[status]}`,
      withoutChip: { ...filters, validationStatuses: filters.validationStatuses.filter((candidate) => candidate !== status) }
    })
  }

  for (const agentId of filters.agentIds) {
    chips.push({
      key: `${AGENT_ID_PARAM}:${agentId}`,
      label: `${AGENT_GROUP_LABEL}: ${getAgentLabel(agentId, agents)}`,
      withoutChip: { ...filters, agentIds: filters.agentIds.filter((candidate) => candidate !== agentId) }
    })
  }

  if (hasDateRange(filters.period)) {
    chips.push({ key: PERIOD_CHIP_KEY, label: getPeriodChipLabel(filters.period), withoutChip: { ...filters, period: EMPTY_DATE_RANGE } })
  }

  return chips
}

export function toggleSubscriptionStatus(filters: CustomerFacetFilters, status: SubscriptionFilter): CustomerFacetFilters {
  const isSelected = filters.subscriptionStatuses.includes(status)
  const next = isSelected
    ? filters.subscriptionStatuses.filter((candidate) => candidate !== status)
    : [...filters.subscriptionStatuses, status]

  return { ...filters, subscriptionStatuses: SUBSCRIPTION_FILTER_VALUES.filter((candidate) => next.includes(candidate)) }
}

export function toggleValidationStatus(filters: CustomerFacetFilters, status: CustomerValidationStatus): CustomerFacetFilters {
  const isSelected = filters.validationStatuses.includes(status)
  const next = isSelected ? filters.validationStatuses.filter((candidate) => candidate !== status) : [...filters.validationStatuses, status]

  return { ...filters, validationStatuses: VALIDATION_STATUS_VALUES.filter((candidate) => next.includes(candidate)) }
}

export function toggleAgentId(filters: CustomerFacetFilters, agentId: number): CustomerFacetFilters {
  const isSelected = filters.agentIds.includes(agentId)
  const next = isSelected ? filters.agentIds.filter((candidate) => candidate !== agentId) : [...filters.agentIds, agentId]

  return { ...filters, agentIds: next }
}

// På och Av utesluter varandra – att klicka i det redan valda slår av filtret helt.
export function toggleShipmentAutomation(
  filters: CustomerFacetFilters,
  value: Exclude<ShipmentAutomationFilter, ''>
): CustomerFacetFilters {
  const next = filters.enablePostnord === value ? '' : value

  return { ...filters, enablePostnord: next }
}
